import { Analytics } from './analytics'

// --- Old events (transitioning — keep until Work Items surface is retired) ---

/** @deprecated Use MeetingAnalytics.scribeConsent instead */
export type MeetingPlatform = 'Teams' | 'Zoom' | 'Meet'
/** @deprecated Use MeetingAnalytics.aiFeedbackGiven instead */
export type MeetingFeedback = 'Good' | 'Bad'

export enum MeetingAnalyticsEvent {
   // Deprecated events
   /** @deprecated Superseded by scribeInvited + scribeStarted */
   meetingJoined = 'Meeting joined',
   /** @deprecated Superseded by scribeConsent */
   confirmConsent = 'Advisor Assistant consent confirmed',
   /** @deprecated Superseded by aiFeedbackGiven */
   meetingFeedback = 'Advisor Assistant feedback',

   // New events
   meetingCreated = 'Meeting created',
   meetingOpened = 'Meeting opened',
   meetingAgendaTemplateChanged = 'Meeting agenda template changed',
   meetingTemplateApplied = 'Meeting template applied',
   meetingAgendaBlockAdded = 'Meeting agenda block added',
   meetingPreworkSent = 'Meeting pre-work sent',
   meetingPreworkReceived = 'Meeting pre-work received',
   meetingPreworkTemplateChanged = 'Meeting pre-work template changed',
   meetingScribeConsent = 'Meeting Scribe consent',
   meetingScribeInvited = 'Meeting Scribe invited',
   meetingScribeCancelled = 'Meeting Scribe cancelled',
   meetingScribeStarted = 'Meeting Scribe started',
   meetingScribeSessionEnded = 'Meeting Scribe session ended',
   meetingDeliverableGenerated = 'Meeting deliverable generated',
   meetingDeliverableEdited = 'Meeting deliverable edited',
   meetingDeliverableShared = 'Meeting deliverable shared',
   meetingDeliverableEditedAfterSharing = 'Meeting deliverable edited after sharing',
   meetingAgendaEdited = 'Meeting agenda edited',
   meetingPreworkEdited = 'Meeting pre-work edited',
   meetingTranscriptPasted = 'Meeting transcript pasted',
   meetingTranscriptEdited = 'Meeting transcript edited',
   meetingAiFeedbackGiven = 'Meeting AI feedback given',
   meetingLeft = 'Meeting left',
   meetingAgentError = 'Meeting agent error',
   meetingsTtvMilestoneReached = 'Meetings TTV milestone reached',
   coachReportGenerated = 'Meetings Coach report generated',
   coachPageOpened = 'Coaching page opened',
   coachReportOpened = 'Meetings Coach report opened',
   onboardingChecklistCompleted = 'Onboarding checklist completed',
}

export namespace MeetingAnalyticsParams {
   /** Flags an activation-reporting event as coming from a demo meeting. */
   type DemoAttribution = {
      isDemo: boolean
   }

   export type Created = {
      initiatedFrom: 'New meeting button' | 'Getting started checklist' | 'Calendar event' | 'Meeting template card' | 'Coaching' | 'Work items banner'
      templateName?: string
      templateSource?: 'Our template' | 'Own template'
   }
   export type Opened = DemoAttribution & {
      entryPoint: 'New meeting button' | 'Existing meeting'
      meetingTemplateName: string
      isFirstMeeting: boolean
   }
   export type AgendaTemplateChanged = {
      templateSource: 'Our template' | 'Own template'
      templateName: string
   }
   export type TemplateApplied = {
      templateSource: 'Our template' | 'Own template'
      templateName: string
   }
   export type AgendaBlockAdded = {
      blockType: string
   }
   export type PreworkSent = {
      templateSource: 'Our template' | 'Own template' | 'Edited our template'
      templateName: string
      sharedVia: 'Email' | 'Link'
      /** Name of the saved message template applied to the personal message, or '' when none was used. */
      messageTemplateName: string
   }
   export type PreworkTemplateChanged = {
      templateSource: 'Our template' | 'Own template'
      templateName: string
   }
   export type TranscriptionMethod = 'bot' | 'in-person'
   export type ScribeConsent = {
      consentOutcome: 'Accepted' | 'Declined'
   }
   export type ScribeStarted = {
      transcriptionMethod: TranscriptionMethod
   }
   export type ScribeSessionEnded = {
      durationMinutes: number
      /** 'Limit reached' is defined but not yet emitted — requires backend to parse Recall.ai sub_code (timeout_exceeded_in_call_recording) */
      reason: 'Paused' | 'Meeting ended' | 'Limit reached' | 'Stopped'
      transcriptionMethod: TranscriptionMethod
   }
   export type DeliverableGenerated = DemoAttribution & {
      deliverableType: string
      generationCount: number
      contextSources: MeetingContextSource[]
      meetingName: string
   }
   export type DeliverableEdited = DemoAttribution & {
      deliverableType: string
   }
   export type DeliverableShared = DemoAttribution & {
      deliverableType: string
   }
   export type DeliverableEditedAfterSharing = DemoAttribution & {
      deliverableType: string
      contextSources: MeetingContextSource[]
      generationCount: number
   }
   export type AgendaEdited = {
      durationMinutes: number
   }
   export type PreworkEdited = {
      durationMinutes: number
   }
   export type AiFeedbackGiven = DemoAttribution & {
      sentiment: 'Good' | 'Bad'
      comment: string
      feedbackTarget: 'Pre-work summary' | 'Scribe transcript' | 'Meeting Minutes' | 'Business Plan' | 'Marketing Plan' | 'Succession Plan' | 'Action Items' | 'Deliverable' | 'Coach report'
      agentName: 'Strategist' | 'Scribe' | 'Producer' | 'Coach'
   }
   export type AgentError = {
      agentName: 'Scribe' | 'Producer'
      errorType: string
   }
   export type CoachReportGenerated = {
      contextSources: MeetingContextSource[]
      generationCount: number
      meetingName: string
   }
   export type CoachPageOpened = {
      reportCount: number
   }
   export type CoachReportOpened = {
      initiatedFrom: 'Coaching page' | 'Meeting workspace'
   }
   export type Left = {
      meetingId: string
      durationMinutes: number
      lastMode: 'Meet' | 'Review'
      completionState: string[]
      transcriptCharCount: number
      agendaCharCount: number
      notesCharCount: number
   }
   export type TtvMilestoneReached = {
      daysToReach: number
      meetingsCompleted: number
   }
}

const AGENT_SCRIBE = 'Scribe'
const AGENT_PRODUCER = 'Producer'

export type MeetingContextSource = 'Scribe transcript' | 'Manual notes' | 'Agenda'

export default class MeetingAnalytics implements Analytics {
   // ---- Deprecated methods (keep until Work Items retired) ----

   /** @deprecated Use scribeInvited + scribeStarted instead */
   static join = (platform: MeetingPlatform) => {
      return Analytics.emitEvent(MeetingAnalyticsEvent.meetingJoined, {
         Platform: platform
      })
   }

   /** @deprecated Use scribeConsent instead */
   static confirmConsent = (email: string) => {
      return Analytics.emitEvent(MeetingAnalyticsEvent.confirmConsent, {
         'User Email': email
      })
   }

   /** @deprecated Use aiFeedbackGiven instead */
   static feedback = (feedback: MeetingFeedback, email: string) => {
      return Analytics.emitEvent(MeetingAnalyticsEvent.meetingFeedback, {
         'Feedback': feedback,
         'User Email': email
      })
   }

   // ---- New meeting workspace events ----

   static created = (params: MeetingAnalyticsParams.Created) => {
      const hasTemplate = !!params.templateName && !!params.templateSource
      return Analytics.emitEvent(MeetingAnalyticsEvent.meetingCreated, {
         'Initiated from': params.initiatedFrom,
         'Template name': hasTemplate ? params.templateName! : 'No template selected',
         'Template source': hasTemplate ? params.templateSource! : 'No template selected',
      })
   }

   static opened = (params: MeetingAnalyticsParams.Opened) => {
      return Analytics.emitEvent(MeetingAnalyticsEvent.meetingOpened, {
         'Entry point': params.entryPoint,
         'Meeting template name': params.meetingTemplateName,
         'Is first meeting': params.isFirstMeeting,
         'Is demo': params.isDemo,
      })
   }

   static agendaTemplateChanged = (params: MeetingAnalyticsParams.AgendaTemplateChanged) => {
      return Analytics.emitEvent(MeetingAnalyticsEvent.meetingAgendaTemplateChanged, {
         'Template source': params.templateSource,
         'Template name': params.templateName,
      })
   }

   static templateApplied = (params: MeetingAnalyticsParams.TemplateApplied) => {
      return Analytics.emitEvent(MeetingAnalyticsEvent.meetingTemplateApplied, {
         'Template source': params.templateSource,
         'Template name': params.templateName,
      })
   }

   static agendaBlockAdded = (params: MeetingAnalyticsParams.AgendaBlockAdded) => {
      return Analytics.emitEvent(MeetingAnalyticsEvent.meetingAgendaBlockAdded, {
         'Block type': params.blockType,
      })
   }

   static preworkSent = (params: MeetingAnalyticsParams.PreworkSent) => {
      return Analytics.emitEvent(MeetingAnalyticsEvent.meetingPreworkSent, {
         'Template source': params.templateSource,
         'Template name': params.templateName,
         'Shared via': params.sharedVia,
         'Message template name': params.messageTemplateName,
      })
   }

   static preworkReceived = () => {
      return Analytics.emitEvent(MeetingAnalyticsEvent.meetingPreworkReceived, {})
   }

   static preworkTemplateChanged = (params: MeetingAnalyticsParams.PreworkTemplateChanged) => {
      return Analytics.emitEvent(MeetingAnalyticsEvent.meetingPreworkTemplateChanged, {
         'Template source': params.templateSource,
         'Template name': params.templateName,
      })
   }

   static scribeConsent = (params: MeetingAnalyticsParams.ScribeConsent) => {
      return Analytics.emitEvent(MeetingAnalyticsEvent.meetingScribeConsent, {
         'Consent outcome': params.consentOutcome,
      })
   }

   static scribeInvited = () => {
      return Analytics.emitEvent(MeetingAnalyticsEvent.meetingScribeInvited, {
         'Agent name': AGENT_SCRIBE,
      })
   }

   static scribeCancelled = () => {
      return Analytics.emitEvent(MeetingAnalyticsEvent.meetingScribeCancelled, {
         'Agent name': AGENT_SCRIBE,
      })
   }

   static scribeStarted = (params?: MeetingAnalyticsParams.ScribeStarted) => {
      return Analytics.emitEvent(MeetingAnalyticsEvent.meetingScribeStarted, {
         'Agent name': AGENT_SCRIBE,
         'Transcription method': params?.transcriptionMethod ?? 'bot',
      })
   }

   static scribeSessionEnded = (params: MeetingAnalyticsParams.ScribeSessionEnded) => {
      return Analytics.emitEvent(MeetingAnalyticsEvent.meetingScribeSessionEnded, {
         'Agent name': AGENT_SCRIBE,
         'Duration minutes': params.durationMinutes,
         'Reason': params.reason,
         'Transcription method': params.transcriptionMethod ?? 'bot',
      })
   }

   static deliverableGenerated = (params: MeetingAnalyticsParams.DeliverableGenerated) => {
      return Analytics.emitEvent(MeetingAnalyticsEvent.meetingDeliverableGenerated, {
         'Agent name': AGENT_PRODUCER,
         'Deliverable type': params.deliverableType,
         'Generation count': params.generationCount,
         'Context sources': params.contextSources,
         'Is demo': params.isDemo,
         'Meeting name': params.meetingName,
      })
   }

   static deliverableEdited = (params: MeetingAnalyticsParams.DeliverableEdited) => {
      return Analytics.emitEvent(MeetingAnalyticsEvent.meetingDeliverableEdited, {
         'Deliverable type': params.deliverableType,
         'Is demo': params.isDemo,
      })
   }

   static deliverableShared = (params: MeetingAnalyticsParams.DeliverableShared) => {
      return Analytics.emitEvent(MeetingAnalyticsEvent.meetingDeliverableShared, {
         'Deliverable type': params.deliverableType,
         'Is demo': params.isDemo,
      })
   }

   static deliverableEditedAfterSharing = (params: MeetingAnalyticsParams.DeliverableEditedAfterSharing) => {
      return Analytics.emitEvent(MeetingAnalyticsEvent.meetingDeliverableEditedAfterSharing, {
         'Deliverable type': params.deliverableType,
         'Context sources': params.contextSources,
         'Generation count': params.generationCount,
         'Is demo': params.isDemo,
      })
   }

   static agendaEdited = (params: MeetingAnalyticsParams.AgendaEdited) => {
      return Analytics.emitEvent(MeetingAnalyticsEvent.meetingAgendaEdited, {
         'Duration minutes': params.durationMinutes,
      })
   }

   static transcriptPasted = () => {
      return Analytics.emitEvent(MeetingAnalyticsEvent.meetingTranscriptPasted, {})
   }

   static preworkEdited = (params: MeetingAnalyticsParams.PreworkEdited) => {
      return Analytics.emitEvent(MeetingAnalyticsEvent.meetingPreworkEdited, {
         'Duration minutes': params.durationMinutes,
      })
   }

   static transcriptEdited = () => {
      return Analytics.emitEvent(MeetingAnalyticsEvent.meetingTranscriptEdited, {})
   }

   static aiFeedbackGiven = (params: MeetingAnalyticsParams.AiFeedbackGiven) => {
      return Analytics.emitEvent(MeetingAnalyticsEvent.meetingAiFeedbackGiven, {
         'Sentiment': params.sentiment,
         'Comment': params.comment,
         'Feedback target': params.feedbackTarget,
         'Agent name': params.agentName,
         'Is demo': params.isDemo,
      })
   }

   static coachReportGenerated = (params: MeetingAnalyticsParams.CoachReportGenerated) => {
      return Analytics.emitEvent(MeetingAnalyticsEvent.coachReportGenerated, {
         'Agent name': 'Coach',
         'Context sources': params.contextSources,
         'Generation count': params.generationCount,
         'Meeting name': params.meetingName,
      })
   }

   static coachPageOpened = (params: MeetingAnalyticsParams.CoachPageOpened) => {
      return Analytics.emitEvent(MeetingAnalyticsEvent.coachPageOpened, {
         'Report count': params.reportCount,
      })
   }

   static coachReportOpened = (params: MeetingAnalyticsParams.CoachReportOpened) => {
      return Analytics.emitEvent(MeetingAnalyticsEvent.coachReportOpened, {
         'Initiated from': params.initiatedFrom,
      })
   }

   static agentError = (params: MeetingAnalyticsParams.AgentError) => {
      return Analytics.emitEvent(MeetingAnalyticsEvent.meetingAgentError, {
         'Agent name': params.agentName,
         'Error type': params.errorType,
      })
   }

   static left = (params: MeetingAnalyticsParams.Left) => {
      return Analytics.emitEvent(MeetingAnalyticsEvent.meetingLeft, {
         'Meeting ID': params.meetingId,
         'Duration minutes': params.durationMinutes,
         'Last mode': params.lastMode,
         'Completion state': params.completionState,
         'Transcript char count': params.transcriptCharCount,
         'Agenda char count': params.agendaCharCount,
         'Notes char count': params.notesCharCount,
      }, { immediateAndAsync: true })
   }

   static ttvMilestoneReached = (params: MeetingAnalyticsParams.TtvMilestoneReached) => {
      return Analytics.emitEvent(MeetingAnalyticsEvent.meetingsTtvMilestoneReached, {
         'Days to reach': params.daysToReach,
         'Meetings completed': params.meetingsCompleted,
      })
   }

   static onOnboardingChecklistCompleted = () => {
      return Analytics.emitEvent(MeetingAnalyticsEvent.onboardingChecklistCompleted, {})
   }
}
